/**
 * Generated by orval 🍺
 * Do not edit manually.
 * Reelplicate API
 * OpenAPI spec version: 1.0
 */
import { useMutation, useQuery } from '@tanstack/react-query';
import type {
  DataTag,
  DefinedInitialDataOptions,
  DefinedUseQueryResult,
  MutationFunction,
  QueryClient,
  QueryFunction,
  QueryKey,
  UndefinedInitialDataOptions,
  UseMutationOptions,
  UseMutationResult,
  UseQueryOptions,
  UseQueryResult,
} from '@tanstack/react-query';

import type { ApiErrorDto, BrandProfileDto, UpdateBrandProfileDto } from '../../model';

import { customInstance } from '../../../mutator';
import type { ErrorType, BodyType } from '../../../mutator';

const withQueryKey = <T extends object, K>(
  query: T,
  queryKey: K,
): T & { queryKey: K } => {
  const result = { queryKey } as T & { queryKey: K };
  for (const key of Object.keys(query)) {
    // The explicit queryKey always wins, matching the previous
    // `{ ...query, queryKey }` spread where it was set last.
    if (key === 'queryKey') continue;
    Object.defineProperty(result, key, {
      enumerable: true,
      configurable: true,
      get: () => (query as Record<string, unknown>)[key],
    });
  }
  return result;
};

/**
 * @summary The workspace's brand profile, including whether its website is still being read.
 */
export const getBrandProfile = (id: string, signal?: AbortSignal) => {
  return customInstance<BrandProfileDto>({
    url: `/api/v1/workspaces/${id}/brand-profile`,
    method: 'GET',
    signal,
  });
};

export const getGetBrandProfileQueryKey = (id: string) => {
  return [`/api/v1/workspaces/${id}/brand-profile`] as const;
};

export const getGetBrandProfileQueryOptions = <
  TData = Awaited<ReturnType<typeof getBrandProfile>>,
  TError = ErrorType<ApiErrorDto>,
>(
  id: string,
  options?: {
    query?: Partial<
      UseQueryOptions<Awaited<ReturnType<typeof getBrandProfile>>, TError, TData>
    >;
  },
) => {
  const { query: queryOptions } = options ?? {};

  const queryKey = queryOptions?.queryKey ?? getGetBrandProfileQueryKey(id);

  const queryFn: QueryFunction<Awaited<ReturnType<typeof getBrandProfile>>> = ({
    signal,
  }) => getBrandProfile(id, signal);

  return {
    queryKey,
    queryFn,
    enabled: id !== null && id !== undefined,
    ...queryOptions,
  } as UseQueryOptions<Awaited<ReturnType<typeof getBrandProfile>>, TError, TData> & {
    queryKey: DataTag<QueryKey, TData, TError>;
  };
};

export type GetBrandProfileQueryResult = NonNullable<
  Awaited<ReturnType<typeof getBrandProfile>>
>;
export type GetBrandProfileQueryError = ErrorType<ApiErrorDto>;

export function useGetBrandProfile<
  TData = Awaited<ReturnType<typeof getBrandProfile>>,
  TError = ErrorType<ApiErrorDto>,
>(
  id: string,
  options: {
    query: Partial<
      UseQueryOptions<Awaited<ReturnType<typeof getBrandProfile>>, TError, TData>
    > &
      Pick<
        DefinedInitialDataOptions<
          Awaited<ReturnType<typeof getBrandProfile>>,
          TError,
          Awaited<ReturnType<typeof getBrandProfile>>
        >,
        'initialData'
      >;
  },
  queryClient?: QueryClient,
): DefinedUseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
export function useGetBrandProfile<
  TData = Awaited<ReturnType<typeof getBrandProfile>>,
  TError = ErrorType<ApiErrorDto>,
>(
  id: string,
  options?: {
    query?: Partial<
      UseQueryOptions<Awaited<ReturnType<typeof getBrandProfile>>, TError, TData>
    > &
      Pick<
        UndefinedInitialDataOptions<
          Awaited<ReturnType<typeof getBrandProfile>>,
          TError,
          Awaited<ReturnType<typeof getBrandProfile>>
        >,
        'initialData'
      >;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
export function useGetBrandProfile<
  TData = Awaited<ReturnType<typeof getBrandProfile>>,
  TError = ErrorType<ApiErrorDto>,
>(
  id: string,
  options?: {
    query?: Partial<
      UseQueryOptions<Awaited<ReturnType<typeof getBrandProfile>>, TError, TData>
    >;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
/**
 * @summary The workspace's brand profile, including whether its website is still being read.
 */

export function useGetBrandProfile<
  TData = Awaited<ReturnType<typeof getBrandProfile>>,
  TError = ErrorType<ApiErrorDto>,
>(
  id: string,
  options?: {
    query?: Partial<
      UseQueryOptions<Awaited<ReturnType<typeof getBrandProfile>>, TError, TData>
    >;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> } {
  const queryOptions = getGetBrandProfileQueryOptions(id, options);

  const query = useQuery(queryOptions, queryClient) as UseQueryResult<TData, TError> & {
    queryKey: DataTag<QueryKey, TData, TError>;
  };

  return withQueryKey(query, queryOptions.queryKey);
}

/**
 * @summary Save the user's brand profile; later website reads won't overwrite it.
 */
export const updateBrandProfile = (
  id: string,
  updateBrandProfileDto: BodyType<UpdateBrandProfileDto>,
  signal?: AbortSignal,
) => {
  return customInstance<BrandProfileDto>({
    url: `/api/v1/workspaces/${id}/brand-profile`,
    method: 'PUT',
    headers: { 'Content-Type': 'application/json' },
    data: updateBrandProfileDto,
    signal,
  });
};

export const getUpdateBrandProfileMutationKey = () => ['updateBrandProfile'] as const;

export const getUpdateBrandProfileMutationOptions = <
  TError = ErrorType<ApiErrorDto>,
  TContext = unknown,
>(options?: {
  mutation?: UseMutationOptions<
    Awaited<ReturnType<typeof updateBrandProfile>>,
    TError,
    UpdateBrandProfileMutationVariables,
    TContext
  >;
}): UseMutationOptions<
  Awaited<ReturnType<typeof updateBrandProfile>>,
  TError,
  UpdateBrandProfileMutationVariables,
  TContext
> => {
  const mutationKey = getUpdateBrandProfileMutationKey();
  const { mutation: mutationOptions } = options
    ? options.mutation &&
      'mutationKey' in options.mutation &&
      options.mutation.mutationKey
      ? options
      : { ...options, mutation: { ...options.mutation, mutationKey } }
    : { mutation: { mutationKey } };

  const mutationFn: MutationFunction<
    Awaited<ReturnType<typeof updateBrandProfile>>,
    UpdateBrandProfileMutationVariables
  > = (props) => {
    const { id, data } = props ?? {};

    return updateBrandProfile(id, data);
  };

  return { mutationFn, ...mutationOptions };
};

export type UpdateBrandProfileMutationResult = NonNullable<
  Awaited<ReturnType<typeof updateBrandProfile>>
>;
export type UpdateBrandProfileMutationBody = BodyType<UpdateBrandProfileDto>;
export type UpdateBrandProfileMutationError = ErrorType<ApiErrorDto>;
export type UpdateBrandProfileMutationVariables = {
  id: string;
  data: BodyType<UpdateBrandProfileDto>;
};

/**
 * @summary Save the user's brand profile; later website reads won't overwrite it.
 */
export const useUpdateBrandProfile = <
  TError = ErrorType<ApiErrorDto>,
  TContext = unknown,
>(
  options?: {
    mutation?: UseMutationOptions<
      Awaited<ReturnType<typeof updateBrandProfile>>,
      TError,
      UpdateBrandProfileMutationVariables,
      TContext
    >;
  },
  queryClient?: QueryClient,
): UseMutationResult<
  Awaited<ReturnType<typeof updateBrandProfile>>,
  TError,
  UpdateBrandProfileMutationVariables,
  TContext
> => {
  return useMutation(getUpdateBrandProfileMutationOptions(options), queryClient);
};
